// @flow
import * as React from 'react';
import moment from 'moment';
import { css, keyframes } from '@emotion/core';

import type { Client } from '../../../ShowPage/APIClient';
import {PrimaryLink} from "../../../Core/components/Buttons";

import type {Airing, Episode, Genre, Show} from "../../Objects";
import type {ShowPage} from "../../../ShowPage/Objects";
import colors from "../../../Styles/colors";
import mq from "../../../Styles/breakpoints";
import {fontWeight} from "../../../Styles/text";
import {visuallyHidden} from "../../../Styles/accessibility";
import {makeCancelable} from "../../../Utilities/helpers";
import type {CancellablePromise} from "../../../Utilities/helpers";
import {getWindow} from "../../../Helpers/browsers";
import {event} from "../../../Helpers/ga";
import {APP_NAME} from "../../App";

let indi = css`
  margin: 0 2px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: ${colors.coreColor2};
`;

let genre = css`
  background: ${colors.coreColor1};
`;

let rating = css`
  background: ${colors.accent};
`;

export class GenreIndicator extends React.PureComponent<Genre> {
  render() {
    return <span css={[indi, genre]}>{this.props.genretxt}</span>;
  }
}

export class RatingIndicator extends React.PureComponent<{| rating: string |}> {
  render() {
    return <span css={[indi, rating]}>{this.props.rating}</span>;
  }
}

let entrance = keyframes`
  0% {
    transform: scale(1.1);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

let expWrapper = css`
  position: relative;
  width: 100%;
  max-height: 100%;
  padding: 16px;
  background: ${colors.coreColor2};
  border: 2px solid ${colors.accent};
  z-index: 2;
  animation: ${entrance} 250ms ease;
  display: -ms-grid;
  display: grid;
  
  grid-template-columns: [col] minmax(1fr, auto) [col] 1fr [col] 1fr;
  grid-template-rows: [row] 0 [row] max-content [row] max-content [row] 1fr [row] max-content;
  //grid-gap: 8px;
  
  ${mq.large(`
    z-index: 4;
    animation: none;
    
    grid-template-columns: [col] 240px [col] 1fr [col] 1fr;
    grid-template-rows: [row] max-content [row] max-content [row] max-content [row] auto [row] auto;
    
    -ms-grid-columns: 240px 1fr 1fr;
    -ms-grid-rows: max-content max-content max-content auto auto;
  `)}
`;

let expThumbnail = css`
  display: none;
  
  ${mq.large(`
    max-width: 240px;
    padding: 0 8px 8px 0;
    display: block;
    
    grid-column: 1;
    grid-row: 3;
    
    -ms-grid-column: 1;
    -ms-grid-row: 3;
  `)}
`;

let expShowTitle = css`
  grid-column: 1 / 4;
  grid-row: 2;
  
  -ms-grid-column: 1;
  -ms-grid-column-span: 3;
  -ms-grid-row: 2;
  
  ${mq.large(`
    grid-column: 1 / 4;
    grid-row: 1;
    
    -ms-grid-column: 1;
    -ms-grid-column-span: 3;
    -ms-grid-row: 1;
  `)};
  
  font-weight: ${fontWeight.bold};
  line-height: 1;
`;

let expEpisodeTitle = css`
  padding: 8px 0;
  grid-column: 1 / 4;
  grid-row: 3;
  
  -ms-grid-column: 1;
  -ms-grid-column-span: 3;
  -ms-grid-row: 3;
  
  ${mq.large(`
    grid-column: 1 / 4;
    grid-row: 2;
    
    -ms-grid-column: 1;
    -ms-grid-column-span: 3;
    -ms-grid-row: 2;
  `)};
  
  font-size: 0.875rem;
  line-height: 1;
`;

let expContent = css`
  overflow: hidden;
  display: grid;
  display: -ms-grid;
  
  grid-template-rows: [row] 1fr [row] max-content [row] max-content;
  grid-column: 1 / 4;
  grid-row: 4;
  
  -ms-grid-rows: 1fr max-content max-content;
  -ms-grid-column: 1;
  -ms-grid-column-span: 3;
  -ms-grid-row: 4;
  
  ${mq.large(`
    padding-left: 8px;
    overflow: visible;
    display: block;
    
    grid-template-rows: none;
    grid-column: 2 / 4;
    grid-row: 3 / 6;
    
    -ms-grid-rows: none;
    -ms-grid-column: 2;
    -ms-grid-column-span: 2;
    -ms-grid-row: 3;
    -ms-grid-row-span: 3;
  `)}
`;

let expDescription = css`
  margin-bottom: 8px;
  font-size: 0.875rem;
  overflow: hidden;
`;

let expAirtime = css`
  margin-bottom: 8px;
  font-size: 0.75rem;
  font-weight: ${fontWeight.bold};
`;

let expContentIndicators = css``;

let buttonEntrance = keyframes`
  0% {
    opacity: 0;
  }
  
  100% {
    opacity: 1;
  }
`;

let expShowButton = css`
  margin: 8px 0 0;
  animation: ${buttonEntrance} 1000ms ease;
  
  grid-column: 1 / 4;
  grid-row: 5;
  
  -ms-grid-column: 1;
  -ms-grid-column-span: 3;
  -ms-grid-row: 5;
  
  ${mq.large(`
    margin: 0 8px 0 0;
    grid-column: 1;
    grid-row: 4;
    
    -ms-grid-column: 1;
    -ms-grid-column-span: 1;
    -ms-grid-row: 4;
  `)}
`;

let expClose = css`
  position: absolute;
  top: 0;
  right: 0;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  background: ${colors.accent};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.625rem;
  font-weight: ${fontWeight.bold};
  line-height: 1;
  
  &:hover {
    background: ${colors.accentHover};
  }
`;

type WPShow = {|
  pageUrl: string,
  thumbnailUrl: string
|}

type InnerProps = {|
  airing: Airing,
  episode: Episode,
  show: Show,
  wpShow: WPShow | null,
  onClose: Function,
  keyboardEnabled: bool
|}

export function getEpisodeLine(episode: Episode): string {
  let epsTitle = episode.title != null ? ` - ${episode.title}` : '';

  return `#${episode.number}${epsTitle}`;
}

export function getHumanDuration(airing: Airing): string {
  let totalMinutes = Math.floor(airing.duration / 60);
  let hours = Math.floor(totalMinutes / 60);
  let remainingMinutes = Math.floor(totalMinutes - hours * 60);

  let timeParts = [];

  if (hours > 0) {
    timeParts.push(`${hours} hr`)
  }

  if (remainingMinutes > 0) {
    timeParts.push(`${remainingMinutes} mins`)
  }

  return timeParts.join(' ');
}

export function getHumanAirtime(airing: Airing): string {
  return `${moment.unix(airing.date).format('h:mmA')} - ${moment.unix(airing.end_date).format('h:mmA')}`;
}

export class ExpansionInner extends React.Component<InnerProps> {
  ref: { current: null | React.ElementRef<'article'> };

  constructor(props: InnerProps) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidMount(): void {
    if (this.ref.current) {

      getWindow().then(window => {

        // Force async here to avoid some of the jank from the expansion popping
        // in and attempting to scroll at the same time
        setTimeout(() => {
          if (this.ref.current && window.innerWidth > 640) {
            const top = this.ref.current.getBoundingClientRect().top + window.pageYOffset;
            const middle = top - (window.innerHeight / 2);

            window.scroll({
              top: middle,
              behavior: 'smooth'
            });

            setTimeout(() => {
              if (this.ref.current) {
                this.ref.current.focus();
              }
            }, 500);
          }
        }, 0);
      });
    }
  }

  handleEscape(e: SyntheticKeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape') {
      this.props.onClose();
    }
  }

  onMoreInfo() {
    let { show, wpShow } = this.props;
    event(APP_NAME, 'Click More Show Info', `${show.title} | ${wpShow && wpShow.pageUrl || ''}`);
  }

  renderThumbnail() {
    let { show, wpShow, disableLinks } = this.props;

    if (wpShow && !disableLinks) {
      return <a css={expThumbnail} href={wpShow.pageUrl} tabIndex={-1} aria-hidden={true} dangerouslySetInnerHTML={{__html: wpShow.thumbnailUrl}}/>;
    }

    return <img css={expThumbnail} src="/content/themes/tpt/dist/images/default-post-image-320.jpg" alt={show.title} aria-hidden={true} />
  }

  renderTitle() {
    let { show, wpShow, disableLinks } = this.props;

    if (wpShow && !disableLinks) {
      return <div css={expShowTitle}>
        <a href={wpShow.pageUrl}>
          {show.title}
        </a>
      </div>;
    } else {
      return <div css={expShowTitle}>{show.title}</div>;
    }
  }

  renderDesc() {
    let { episode, show } = this.props;

    if (episode.desc != null && episode.desc.length > 0) {
      return <div css={expDescription}>{episode.desc}</div>;
    } else if (show.desc != null && show.desc.length > 0) {
      return <div css={expDescription}>{show.desc}</div>;
    }

    return null;
  }

  render() {
    let { airing, episode, show, wpShow, onClose, keyboardEnabled } = this.props;

    let genres = show.genres.concat(episode.genres);
    genres = genres.filter((genre: Genre, index: number) => {
        return genres.findIndex((g: Genre) => genre.genrecd === g.genrecd) === index;
      })
      .sort((g1: Genre, g2: Genre) => {
        return g1.genretxt.localeCompare(g2.genretxt);
      });

    let hideOutline = {
      outline: 'none'
    };

    return <article
      css={expWrapper}
      style={!keyboardEnabled && hideOutline || {}}
      tabIndex={0}
      ref={this.ref}
      onKeyDown={this.handleEscape.bind(this)}
    >
      { this.renderThumbnail() }
      { this.renderTitle() }
      <div css={expEpisodeTitle}>{getEpisodeLine(episode)}</div>
      <div css={expContent}>
        { this.renderDesc() }
        <div css={expAirtime}>Time: {getHumanDuration(airing)}, {getHumanAirtime(airing)}</div>
        <div css={expContentIndicators}>
          <span>
            <span css={visuallyHidden}>Genres</span>
            {
              genres
                .map((genre: Genre, index: number): React.Element<typeof GenreIndicator> => {
                return <GenreIndicator key={index} genrecd={genre.genrecd} genretxt={genre.genretxt} />;
              })
            }
          </span>
          {
            episode.version.rating !== null && episode.version.rating.length > 0 &&
            <span>
              <span css={visuallyHidden}>Rating</span>
              <RatingIndicator rating={episode.version.rating}/>
            </span>
          }
        </div>
      </div>
      {
        !this.props.disableLinks &&
        <PrimaryLink
          css={expShowButton}
          href={wpShow && wpShow.pageUrl || null}
          style={{visibility: wpShow ? 'visible' : 'hidden' }}
          onClick={this.onMoreInfo.bind(this)}>
          More Show Info
        </PrimaryLink>
      }
      <button aria-label="Close" css={expClose} onClick={onClose}>X</button>
    </article>;
  }
}

type State = {|
  wpShow: WPShow | null
|}

type Props = {|
  airing: Airing,
  episode: Episode,
  show: Show,
  onClose: Function,
  showAPIClient: () => Client,
  keyboardEnabled: bool
|}

const blockedProtrackPages = [29997, 34745];

export default class Expansion extends React.Component<Props, State> {
  loader: CancellablePromise<Array<ShowPage>> | null;

  constructor(props: Props) {
    super(props);

    this.state = {
      wpShow: null
    };
  }

  componentDidMount(): void {
    let { show, showAPIClient } = this.props;

    this.loader = makeCancelable(
      showAPIClient().getShowsForProTrackSeries(show.id)
    );

    this.loader
      .then((wpShows: Array<ShowPage>) => {
        if (wpShows.length > 0) {
          this.setState({
            wpShow: {
              pageUrl: `/${wpShows[0].post.post_name}/`,
              thumbnailUrl: wpShows[0].mezzanine
            }
          });
        }
      })
      .catch(() => {
        // Throw away errors
        this.loader = null;
      });
  }

  componentWillUnmount(): void {
    if (this.loader !== null) {
      this.loader.cancel();
      this.loader = null;
    }
  }

  render() {
    let { wpShow } = this.state;
    let { airing, episode, show, keyboardEnabled } = this.props;

    return <ExpansionInner
      airing={airing}
      episode={episode}
      show={show}
      wpShow={wpShow}
      onClose={this.props.onClose}
      keyboardEnabled={keyboardEnabled}
      disableLinks={blockedProtrackPages.indexOf(show.id) !== -1}
    />;
  }
}